/**
 * Domain errors: the closed set of things the system refuses to do, and the one
 * table that says what each one is over HTTP.
 *
 * `ARCHITECTURE.md` §API conventions: "Domain errors are a closed union
 * (`InvoiceNotPayable`, `AllowanceExceeded`, `WidgetPaused` …) mapped to HTTP in one
 * place. Clients see codes, never stack traces."
 *
 * The union is derived from the table rather than declared beside it, so a code
 * without a status is not a thing that can be written. Later sessions append rows;
 * `apps/api/src/http/problem.ts` reads the table and adds nothing to it.
 */
/**
 * Every domain error code, and the HTTP status it maps to.
 *
 * `NeedsReview` is 202 on purpose: the call was accepted and turned into a review
 * task rather than refused (`ARCHITECTURE.md` §Guardrails).
 *
 * The six codes after `ValidationFailed` are the request lifecycle's own refusals
 * (session 03). Every non-2xx body is a `Problem` and `ProblemSchema.code` is this
 * enum, so a 401, a 413, a 415 or an unhandled exception has to have a code here or
 * it cannot be rendered at all. `Internal` is the only one that is not a refusal:
 * it means a bug, and the body carries the `req_id` and nothing else.
 *
 * The three after `ImpersonationForbidden` are session 04's: a seat limit reached,
 * an invitation past its seven days (410 — the invitation existed and is gone), and
 * the configuration error of a staff user who is also a member of a client.
 */
export const DOMAIN_ERROR_STATUS = {
    NotFound: 404,
    Forbidden: 403,
    ValidationFailed: 400,
    Unauthenticated: 401,
    NoActiveOrganization: 403,
    UnsupportedMediaType: 415,
    PayloadTooLarge: 413,
    IdempotencyKeyRequired: 400,
    Internal: 500,
    IllegalTransition: 409,
    Conflict: 409,
    IdempotencyConflict: 409,
    InvoiceNotPayable: 409,
    AllowanceExceeded: 409,
    WidgetPaused: 409,
    LimitReached: 409,
    InsufficientBalance: 409,
    ApprovalRequired: 403,
    ImpersonationForbidden: 403,
    SeatLimit: 409,
    InvitationExpired: 410,
    StaffCannotBeMember: 409,
    NeedsReview: 202,
    /**
     * An OAuth access token that belongs to a real person who is not studio staff
     * (session 05). Distinct from `Forbidden` because it is not about a scope: the MCP
     * door is never open to a client user, and an MCP client that gets this should stop
     * retrying rather than ask for more scopes.
     */
    StaffOnly: 403,
    SlotTaken: 409,
    QuoteExpired: 409,
    RateLimited: 429,
    InvalidPackage: 400,
    /**
     * The files disk is below its free-space threshold (session 06). RFC 4918's 507,
     * which is the one status that says "the request was fine, this server has no room
     * for it" — a client retrying it later is right, and a client changing the request
     * is not.
     *
     * It is checked **before** any bytes are written, so nothing is half-stored; and it
     * is a 5xx on purpose, because a full disk is an operator's problem rather than the
     * caller's, and `http/problem.ts` `isExpected` is what turns that into a Sentry
     * report and an error-level log line.
     */
    InsufficientStorage: 507,
    /**
     * The lease on a task was not this caller's any more (session 07).
     *
     * `.docs/07-tasks.md` §Edge cases: "`heartbeat` after the lease expired and the
     * task was re-opened → `IllegalTransition`-mapped `LeaseLost` (add the code) so the
     * agent stops." A 409 for the same reason `IllegalTransition` is one — the request
     * was well formed and the world had moved — and a code of its own because the
     * branch an agent has to take is different: `IllegalTransition` means *try
     * something else*, and this means **stop working on this task**, somebody else has
     * it now.
     */
    LeaseLost: 409,
    /**
     * A usage event whose `occurred_at` is nowhere near now (session 13).
     *
     * `.docs/13-usage-platform.md` §Edge cases: "`occurred_at` more than 7 days in the
     * past or 1 hour in the future → `400` (`ClockSkew` code) — a widget with a broken
     * clock must not write into a closed month."
     *
     * A code of its own rather than `ValidationFailed`, because the branch the caller
     * has to take is different and the SDK is what takes it: a malformed body is a bug
     * in the widget and will fail again, and this is a **machine whose clock is wrong**
     * — the event is real, the timestamp is not, and the fix is NTP rather than a
     * different payload.
     */
    ClockSkew: 400,
    /**
     * A webhook whose signature is not ours (session 15).
     *
     * `ARCHITECTURE.md` §Security › Webhooks: "Stripe signature and Wise signature
     * verified before anything is stored." A code of its own rather than
     * `ValidationFailed`, because the two say different things to an operator reading
     * a log: a body we could not parse is a provider we do not understand, and this is
     * a request that was **not sent by the provider at all** — a wrong endpoint secret
     * after a key rotation, or somebody posting at the door. Nothing is stored either
     * way, and Stripe and Wise both read a 400 as "do not redeliver this".
     */
    WebhookSignatureInvalid: 400,
    /**
     * A bank account that has not finished verification (session 15).
     *
     * `PAYMENTS.md` §Flow: "Checkout defaults to instant with microdeposit fallback",
     * and microdeposits "take 1–2 business days". A charge against a method in that
     * state is refused by Stripe, so `billing.charge` refuses it first and reschedules
     * itself — which is why this is not `Forbidden` or `ValidationFailed`: the caller
     * did nothing wrong and the answer will change on its own within a week.
     */
    PaymentMethodUnverified: 409,
    /**
     * A config was set on a widget whose type carries no `config_schema` (session 25).
     *
     * `.plans/ai-product-placer/README.md` §4.1: a widget type may carry a JSON Schema
     * for its contract config, and `null` means the type takes no config at all — which
     * is what the chatbot and the booking widget are. A code of its own rather than
     * `ValidationFailed`, because the branch the caller has to take is different: a
     * config that violates the schema is a body to fix, and this is a **widget that has
     * no configuration to set** — the fix is `catalog_update_widget_type` on the type,
     * and it changes nothing for the widgets already created from it.
     */
    ConfigNotSupported: 409,
    /**
     * A request that is well formed and still says nothing this system can keep
     * (session 46): a blog body that is JSON but not a document of the blog's schema, a
     * post that fails a blocking pre-publish check, a form submission whose fields break
     * the form's own rules. RFC 9110's 422 — the syntax was fine and the content was not —
     * and a code of its own rather than `ValidationFailed`, because the caller's branch
     * differs: a 400 is a request to rebuild, and this is content to change, field by
     * field. It carries **`errors`** (`DomainError.errors`), one entry per thing to fix,
     * because a list of reasons is the whole of the answer and a flat `meta` cannot hold
     * one.
     */
    Unprocessable: 422,
    /**
     * A form that has been closed (session 46, `.docs/46-blog-and-forms.md` §6). RFC
     * 9110's 410 — the form existed and has gone on purpose — rather than a 404, because a
     * site that still shows it should stop: the branch is "take the form down", not "fix
     * the address". Every lead it already took is kept; reopening it is one change.
     */
    FormClosed: 410,
};
/** Every code, in table order. `schemas/problem.ts` builds the enum from this. */
export const DOMAIN_ERROR_CODES = Object.keys(DOMAIN_ERROR_STATUS);
/** The HTTP status for a code. One mapper, and this is it. */
export function httpStatusOf(code) {
    return DOMAIN_ERROR_STATUS[code];
}
/**
 * The one error class in this package. Everything thrown by `money`, `dates`,
 * `refs`, `pricing` and `states` is one of these, so a caller can tell a refusal
 * from a bug by its type.
 */
export class DomainError extends Error {
    code;
    detail;
    meta;
    /**
     * Field by field, what to fix — empty except on a refusal that has a list to give
     * (`Unprocessable`). `apps/api/src/http/problem.ts` renders it as the problem's
     * `errors`, and the MCP adapter puts it in the error's `data`.
     */
    errors;
    constructor(code, detail = '', meta = {}, errors = []) {
        super(detail === '' ? code : `${code}: ${detail}`);
        this.name = 'DomainError';
        this.code = code;
        this.detail = detail;
        this.meta = meta;
        this.errors = errors;
    }
    /** The HTTP status this error maps to. */
    get status() {
        return DOMAIN_ERROR_STATUS[this.code];
    }
}
/** True for a refusal this system meant; false for a bug. */
export function isDomainError(value) {
    return value instanceof DomainError;
}
/**
 * What a refusal **answered from the store** ends with: either door's answer to an
 * idempotency key it has already answered (`apps/api/src/actions/registry.ts`), marked
 * `replayed: true` in its meta. Written for a tool caller, who is told how to ask
 * afresh. A person's page says the refusal without it (`apps/web/src/api/problem.ts`
 * `messageOf`): what reaches a person under an old key is a form's press made again
 * after one that had no answer, and the refusal is what became of that one.
 */
export const REPLAYED_REFUSAL_NOTE = '(Not asked again: this is the answer stored for that idempotency key when it was ' +
    'first used, and it may be out of date. Send a key no earlier call has used to ask ' +
    'afresh.)';
//# sourceMappingURL=errors.js.map